import { getVerticalScrollOverflow } from "../../utils/vertical-scroll-overflow.ts"

/**
 * Mount a row's actions menu inside the table's own scroll viewport.
 *
 * On `document.body` the menu is placed once, at viewport coordinates. A table
 * that scrolls its body internally then slides the row out from under it, so
 * the menu is left hanging over whichever row happens to be there — it stops
 * belonging to the row it acts on.
 *
 * Edit-row Select and DatePicker popups portal to `document.body` so the table
 * cannot clip them, and close when their table scrolls horizontally. An actions
 * menu instead lives in the pinned column itself, so it has to travel with its
 * row.
 *
 * Anchoring is conditional on the body actually overflowing, and AntD resolves
 * the container once per open. A table that is short when the menu opens and
 * grows while it is still open therefore keeps `document.body` until the menu
 * is reopened. Accepted: it takes rows arriving without a scroll — a filter or
 * a late load — and costs a stale position on a menu the user can close, where
 * anchoring unconditionally would clip the menu on every short table instead.
 *
 * See `docs/kb/decisions/2026-08-18-row-menu-clips-with-its-scroll-body.md`.
 */
export const getRowActionsPopupContainer = (
  trigger: HTMLElement
): HTMLElement => {
  const body = trigger.closest(".ant-table-body") as HTMLElement | null
  if (!body) return document.body

  // Row heights are fractional, so an exactly-fitting body reports a
  // scrollHeight a pixel over its clientHeight.
  const scrolls = getVerticalScrollOverflow(body) > 1
  return scrolls ? body : document.body
}
